import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logger';

const ASSESSMENT_NAMES: Record<string, string> = {
  'owner-readiness': 'Owner Readiness Assessment',
  'succession-readiness': 'Succession Readiness Assessment',
  'business-profile': 'Business Profile Assessment',
  'business-valuation': 'Business Valuation Assessment',
  'business-optimization': 'Business Optimization Review',
  'advisory-team': 'Advisory Team Assembly',
  'financial-readiness': 'Financial Readiness Assessment',
  'succession-strategy': 'Succession Strategy Assessment',
  'governance-family': 'Governance & Family Dynamics',
  'client-team-transition': 'Client & Team Transition',
  'mysuccession-plan': 'Succession Plan Assembly',
};

interface DbAssessmentCompletionScreenProps {
  question: { id: string; type: string; title: string };
  onFinish: () => void;
}

export function DbAssessmentCompletionScreen({ question, onFinish }: DbAssessmentCompletionScreenProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [totalScore, setTotalScore] = useState<number | null>(null);

  const assessmentKey = question.id.replace(/-completion$/, '');
  const assessmentName = ASSESSMENT_NAMES[assessmentKey] || 'Assessment';

  useEffect(() => {
    if (!user?.id) return;

    const fetchScore = async () => {
      try {
        const { data, error } = await supabase
          .from('mysuccession_scores')
          .select('total_score')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) throw error;
        if (data?.total_score != null) {
          setTotalScore(data.total_score);
        }
      } catch (error) {
        logger.error('Error fetching score on completion:', error);
      }
    };

    fetchScore();
  }, [user?.id]);

  return (
    <div className="text-center space-y-8 py-8 w-full max-w-4xl mx-auto min-h-[400px] flex flex-col items-center justify-center">
      <div className="flex justify-center">
        <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
          <CheckCircle2 className="h-8 w-8 text-primary" />
        </div>
      </div>

      <div className="space-y-3">
        <h2 className="text-2xl font-semibold text-foreground">
          {assessmentName} Complete
        </h2>

        {totalScore != null && (
          <p className="text-lg text-muted-foreground">
            Your mySuccession Score: <span className="font-bold text-foreground">{totalScore}%</span>
          </p>
        )}

        <p className="text-muted-foreground max-w-lg mx-auto">
          Your succession plan has been updated. View your dashboard to see your progress and next recommended steps.
        </p>
      </div>

      <div className="flex flex-col items-center gap-3">
        <Button
          onClick={() => navigate('/dashboard?overlay=score')}
          className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-2 h-auto text-sm"
        >
          View Your Score
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={onFinish}
          className="text-muted-foreground hover:text-foreground"
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
